<template>
  <component :is="!(item.disabled && !isDev) && item.path ? 'router-link' : 'span'" :to="item.path">
    <div
      :class="[
        'tw:body-2 tw:m-0 tw:flex tw:gap-3 tw:items-center tw:truncate tw:rounded-sm tw:select-none tw:h-8 tw:min-w-10 tw:py-2 tw:border tw:border-transparent tw:transition-all tw:duration-200 tw:ease-out',
        active
          ? 'tw:border-[rgba(163,163,0,0.32)] tw:bg-[rgba(224,224,0,0.24)]'
          : item.disabled
          ? ''
          : 'tw:hover:bg-[rgba(0,0,0,0.04)]',
        activeChildren ? 'tw:bg-[rgba(0,0,0,0.04)]' : '',
        item.disabled ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer',
        isShrink ? 'tw:px-1.5 tw:min-w-6' : 'tw:px-2',
      ]"
      @click="emit('click')"
    >
      <div
        v-if="isSubMenuItem"
        :class="[
          'tw:absolute tw:top-0 tw:-left-[0.4375rem] tw:w-0.5 tw:h-8 tw:bg-transparent tw:transition-colors tw:duration-300',
          active ? 'tw:!bg-black' : '',
        ]"
      />
      <LiveEventsIcon v-if="item.iconName === 'rec-live-events'" :show-background="false" size="small" />
      <EdroneAiSparklesIcon v-else-if="item.iconName === 'sparkles'" class="tw:-mr-1" />
      <div
        v-else-if="item.iconName"
        :class="labelClassList"
        class="tw:body-1 tw:w-4 tw:h-4 tw:flex tw:justify-center tw:items-center"
      >
        <font-awesome-icon :icon="item.iconName" />
      </div>

      <template v-if="!isShrink">
        <span :class="labelClassList" class="tw:truncate tw:min-w-0">
          {{ item.label }}
        </span>
        <div class="tw:ms-auto tw:flex tw:gap-3 tw:items-center tw:!font-normal">
          <font-awesome-icon v-if="hasNewFeature" class="tw:text-purple-500" icon="fa-solid fa-fire" />
          <slot name="badge" />
          <font-awesome-icon v-if="item.hidden" class="tw:text-default-700 tw:text-2xl" icon="fa-brands fa-dev" />

          <EdroneButtonIcon
            v-if="item.disabled"
            :unique-id="'main-menu__lock-button--' + item.name"
            class="tw:pointer-events-none"
            size="small"
            type="border"
          >
            <font-awesome-icon icon="fa-light fa-lock" />
          </EdroneButtonIcon>

          <template v-if="isMobile">
            <span
              v-if="item.children?.length && !item.disabled"
              class="tw:relative tw:w-3 tw:h-3 tw:flex tw:items-center tw:justify-center"
              @click.stop="emit('click')"
            >
              <font-awesome-icon
                :class="showChildren ? 'tw:rotate-180' : ''"
                class="tw:w-3 tw:h-3 tw:transition-transform tw:duration-300"
                icon="fa-light fa-caret-down"
              />
            </span>
            <font-awesome-icon v-if="item.name === 'logout'" :icon="item.iconName" @click.stop="emit('click')" />
          </template>
          <template v-else>
            <span
              v-if="item.children?.length && !item.disabled"
              class="tw:relative tw:w-3 tw:h-3"
              @click.stop="emit('click')"
            >
              <Transition
                enter-active-class="tw:transition-opacity tw:duration-150 tw:ease-out"
                enter-from-class="tw:opacity-0"
                leave-active-class="tw:transition-opacity tw:duration-150 tw:ease-out"
                leave-to-class="tw:opacity-0"
              >
                <font-awesome-icon
                  :class="showChildren ? 'tw:rotate-180' : ''"
                  class="tw:absolute tw:flex tw:justify-center tw:items-center tw:w-3 tw:h-3 tw:transition-transform tw:duration-300"
                  icon="fa-light fa-caret-down"
                />
              </Transition>
            </span>
          </template>
        </div>
      </template>
    </div>
  </component>
</template>

<script lang="ts" setup>
import { computed, defineEmits } from "vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import LiveEventsIcon from "vue-app/components/common/LiveEventsIcon.vue";
import EdroneAiSparklesIcon from "edrone-components/EdroneAiSparklesIcon.vue";
import { MenuItem } from "vue-app/components/MainMenu/MainMenu.helpers.ts";
import { isNewFeatureActive } from "vue-app/utils/functions";

const props = defineProps<{
  item: MenuItem;
  active?: boolean;
  activeChildren?: boolean;
  isMobile?: boolean;
  isShrink?: boolean;
  showChildren?: boolean;
  isSubMenuItem?: boolean;
}>();
const emit = defineEmits(["click"]);
const isDev = window.EDRONE_CONTEXT.user.isDev;
const hasNewFeature = computed(() => isNewFeatureActive(props.item.newFeatureRangeDate));
const labelClassList = computed(() => [
  "tw:text-black",
  props.isSubMenuItem ? "tw:text-default-700" : "",
  props.item.disabled ? "tw:!text-default-500" : "",
  props.active ? "tw:!text-black tw:font-medium" : "",
]);
</script>
